/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */
var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.dashboard.item",
  title: "Dashboard List Item",
  description: "A list item for dashboard list views. Includes title, subtitle, metadata, badge, and edit/delete/action buttons.",
  styling: {
    styles: [
      {
        selector: ".ua-dashboard-item",
        styles: [
          "display: flex;",
          "align-items: center;",
          "gap: .75rem;",
          "padding: .65rem .85rem;",
          "border-radius: .5rem;",
          "border: 1px solid var(--bs-border-color);",
          "background: var(--bs-body-bg);",
          "margin-bottom: .35rem;",
          "transition: background .15s ease;"
        ]
      },
      {
        selector: ".ua-dashboard-item:hover",
        styles: [
          "background: var(--bs-secondary-bg);"
        ]
      },
      {
        selector: ".ua-dashboard-item-icon",
        styles: [
          "flex: 0 0 2rem;",
          "width: 2rem;",
          "height: 2rem;",
          "border-radius: .4rem;",
          "background: var(--bs-secondary-bg);",
          "display: flex;",
          "align-items: center;",
          "justify-content: center;",
          "font-size: 1rem;",
          "color: var(--bs-secondary-color);"
        ]
      },
      {
        selector: ".ua-dashboard-item-body",
        styles: [
          "flex: 1 1 0;",
          "min-width: 0;",
          "overflow: hidden;"
        ]
      },
      {
        selector: ".ua-dashboard-item-title",
        styles: [
          "font-weight: 600;",
          "white-space: nowrap;",
          "overflow: hidden;",
          "text-overflow: ellipsis;"
        ]
      },
      {
        selector: ".ua-dashboard-item-subtitle",
        styles: [
          "font-size: .8rem;",
          "color: var(--bs-secondary-color);",
          "white-space: nowrap;",
          "overflow: hidden;",
          "text-overflow: ellipsis;"
        ]
      },
      {
        selector: ".ua-dashboard-item-meta",
        styles: [
          "display: flex;",
          "gap: .5rem;",
          "flex-wrap: wrap;",
          "margin-top: .2rem;"
        ]
      },
      {
        selector: ".ua-dashboard-item-actions",
        styles: [
          "flex: 0 0 auto;",
          "display: flex;",
          "gap: .25rem;",
          "align-items: center;"
        ]
      },
      {
        device: "iphone",
        selector: ".ua-dashboard-item",
        styles: [
          "flex-wrap: wrap;",
          "gap: .5rem;"
        ]
      },
      {
        device: "iphone",
        selector: ".ua-dashboard-item-actions",
        styles: [
          "flex: 0 0 100%;",
          "justify-content: flex-end;"
        ]
      }
    ]
  },
  fields: {
    "title": {
      title: "Title",
      type: "string",
      description: "The main title of the item.",
      required: true
    },
    "subtitle": {
      title: "Subtitle",
      type: "string",
      description: "Secondary line beneath the title."
    },
    "icon": {
      title: "Icon",
      type: "string",
      description: "Bootstrap icon class (e.g. bi-person). Shown as a square glyph on the left."
    },
    "badge": {
      title: "Badge",
      type: "string",
      description: "Optional badge text (e.g. status label)."
    },
    "badge-class": {
      title: "Badge Class",
      type: "string",
      description: "Bootstrap badge class (e.g. bg-success). Defaults to bg-secondary.",
      default: "bg-secondary"
    },
    "meta": {
      title: "Meta",
      type: "inner",
      description: "Additional metadata rendered as small text below the subtitle."
    },
    "edit-href": {
      title: "Edit URL",
      type: "string",
      description: "URL to navigate to when edit is clicked. Typically /dashboard/<page-ns>/<record-id>."
    },
    "delete-id": {
      title: "Delete ID",
      type: "string",
      description: "The record ID used when confirming deletion. Triggers ua.dialog confirmation before calling DashboardAPI.delete()."
    },
    "delete-label": {
      title: "Delete Confirmation Label",
      type: "string",
      description: "The label shown in the delete confirmation. Defaults to the item title.",
      default: ""
    },
    "page-namespace": {
      title: "Page Namespace",
      type: "string",
      description: "The dashboard page namespace used for delete API calls."
    },
    "actions": {
      title: "Quick Actions",
      type: "array",
      description: "Extra quick action buttons. Each: { label, icon, action, class, href }."
    }
  }
});

var jsonRender = require("../interface/jsonRender.js");

/**
 * Render a dashboard list item.
 */
exports.render = function(options) {
  var title = (options.title || "").toString();
  var subtitle = (options.subtitle || "").toString();
  var icon = (options.icon || "").toString();
  var badge = (options.badge || "").toString();
  var badgeClass = (options["badge-class"] || "bg-secondary").toString();
  var meta = options.meta;
  var editHref = (options["edit-href"] || "").toString();
  var deleteId = (options["delete-id"] || "").toString();
  var deleteLabel = (options["delete-label"] || title).toString();
  var pageNamespace = (options["page-namespace"] || "").toString();
  var actions = options.actions instanceof Array ? options.actions : [];

  // Icon block
  var iconBlock = null;
  if (icon) {
    var iconClass = icon.indexOf("bi-") === 0 ? ("bi " + icon) : ("bi bi-" + icon);
    iconBlock = {
      n: "div",
      c: "ua-dashboard-item-icon",
      i: { n: "i", c: iconClass }
    };
  }

  // Body
  var bodyInner = [
    { n: "div", c: "ua-dashboard-item-title", i: title }
  ];

  if (subtitle) {
    bodyInner.push({ n: "div", c: "ua-dashboard-item-subtitle", i: subtitle });
  }

  if (meta) {
    bodyInner.push({
      n: "div",
      c: "ua-dashboard-item-meta small text-muted",
      i: meta
    });
  }

  var body = {
    n: "div",
    c: "ua-dashboard-item-body",
    i: bodyInner
  };

  // Badge
  var badgeEl = null;
  if (badge) {
    badgeEl = {
      n: "span",
      c: "badge " + badgeClass,
      i: badge
    };
  }

  // Action buttons
  var actionButtons = [];

  // Quick actions
  for (var a = 0; a < actions.length; a++) {
    var qa = actions[a];
    var qaIcon = (qa.icon || "").toString();
    var qaClass = (qa.class || "btn btn-sm btn-outline-secondary").toString();
    var qaLabel = (qa.label || "").toString();
    var qaInner = [];

    if (qaIcon) {
      var qaIconClass = qaIcon.indexOf("bi-") === 0 ? ("bi " + qaIcon) : ("bi bi-" + qaIcon);
      qaInner.push({ n: "i", c: qaIconClass + (qaLabel ? " me-1" : "") });
    }
    if (qaLabel) qaInner.push(qaLabel);

    if (qa.href) {
      actionButtons.push({
        n: "a",
        href: qa.href,
        c: qaClass,
        title: qaLabel,
        i: qaInner.length === 1 ? qaInner[0] : qaInner
      });
    } else {
      actionButtons.push({
        n: "button",
        type: "button",
        c: qaClass,
        title: qaLabel,
        "data-dashboard-action": (qa.action || "").toString(),
        i: qaInner.length === 1 ? qaInner[0] : qaInner
      });
    }
  }

  // Edit button
  if (editHref) {
    actionButtons.push({
      n: "a",
      href: editHref,
      c: "btn btn-sm btn-outline-primary",
      title: "Edit",
      i: { n: "i", c: "bi bi-pencil" }
    });
  }

  // Delete button — triggers client-side ua.dialog confirmation
  if (deleteId && pageNamespace) {
    actionButtons.push({
      n: "button",
      type: "button",
      c: "btn btn-sm btn-outline-danger",
      title: "Delete",
      "data-dashboard-delete": "true",
      "data-delete-id": deleteId,
      "data-delete-label": deleteLabel,
      "data-page-namespace": pageNamespace,
      i: { n: "i", c: "bi bi-trash3" }
    });
  }

  // Build the item row
  var itemInner = [];
  if (iconBlock) itemInner.push(iconBlock);
  itemInner.push(body);
  if (badgeEl) itemInner.push(badgeEl);
  if (actionButtons.length > 0) {
    itemInner.push({
      n: "div",
      c: "ua-dashboard-item-actions",
      i: actionButtons
    });
  }

  return {
    n: "div",
    c: "ua-dashboard-item",
    i: itemInner
  };
};
